/**
 * Research content (trading school, exam write-ups, saved strategies),
 * bundled from the repo's docs/ folder at build time. The docs stay the one
 * source of truth: edit them, rebuild, and the pages follow.
 */

import { marked } from 'marked';
import notebookRaw from '../../../.claude/skills/trading-school/SKILL.md?raw';
import examReadmeRaw from '../../../docs/exam/README.md?raw';
import schoolReadmeRaw from '../../../docs/school/README.md?raw';

const lessonJson = import.meta.glob<LessonDef>('../../../docs/school/level-*/*/lesson.json', {
  eager: true,
  import: 'default',
});
const lessonResults = import.meta.glob<unknown>('../../../docs/school/level-*/*/results.json', {
  eager: true,
  import: 'default',
});
const lessonMd = import.meta.glob<string>('../../../docs/school/level-*/*/lesson.md', {
  eager: true,
  query: '?raw',
  import: 'default',
});
import blueprintRaw from '../../../docs/manager/AI_FUND_MANAGER_BLUEPRINT.md?raw';

const levelMd = import.meta.glob<string>('../../../docs/school/level-*/*.md', {
  eager: true,
  query: '?raw',
  import: 'default',
});
const strategyJson = import.meta.glob<Record<string, unknown>>(
  ['../../../docs/exam/*.json', '../../../docs/school/strategies/*.json', '../../../docs/school/funds/*.json'],
  { eager: true, import: 'default' },
);

export interface LessonDef {
  id: string;
  title: string;
  claim: string;
}

export interface ExperimentMetrics {
  trades: number;
  totalReturnPct: number;
  maxDrawdownPct: number;
  profitableMonthsPct: number;
  worstTradePct: number;
  sharpe: number;
  /** Compounded average month, in % (lessons graded from Level 24 on). */
  avgMonthPct?: number;
}

/** One check's evidence. Rigorous runs add confidence, periods and a verdict. */
export interface CheckResult {
  text: string;
  passed: boolean | null;
  leftValue: number;
  op: string;
  rightValue: number;
  /** Share of bootstrap resamples where the check held; null when not resampled. */
  confidence?: number | null;
  periods?: { name: string; passed: boolean | null }[];
  verdict?: 'holds' | 'fails' | 'uncertain';
}

/** Results for one market (or the lesson's own universe, "default"). */
export interface ResultGroup {
  verdict?: string;
  checks: CheckResult[];
  experiments: Record<string, ExperimentMetrics>;
}

export interface LessonResults {
  runAt: string;
  verdict?: string;
  draft?: boolean;
  preregistered?: { commit: string; committedAt: string } | null;
  groups: Record<string, ResultGroup>;
  /** Unusual engine warnings by experiment and kind (rejected orders, positions left open...). */
  engineWarnings?: Record<string, Record<string, number>>;
}

/** Results files written before the rigorous runner had no groups. */
function normalizeResults(raw: unknown): LessonResults | null {
  if (!raw) return null;
  const r = raw as Partial<LessonResults> & { checks?: CheckResult[]; experiments?: Record<string, ExperimentMetrics> };
  if (r.groups) return r as LessonResults;
  return { runAt: r.runAt ?? '', groups: { default: { checks: r.checks ?? [], experiments: r.experiments ?? {} } } };
}

export const MARKET_LABELS: Record<string, string> = {
  default: '',
  fx: 'Currencies and gold',
  stocks: 'US index funds',
  crypto: 'Bitcoin and Ether',
};

export type LessonStatus =
  | 'Proven'
  | 'Partly true'
  | 'Not proven'
  | 'Rejected'
  | 'Varies by market'
  | 'Mastered'
  | 'Mastered (weak)'
  | 'Not run';

const STATUSES = new Set<string>([
  'Proven',
  'Partly true',
  'Not proven',
  'Rejected',
  'Varies by market',
  'Mastered',
  'Mastered (weak)',
]);

export interface Lesson extends LessonDef {
  folder: string;
  status: LessonStatus;
  results: LessonResults | null;
  html: string;
}

export interface Level {
  number: number;
  title: string;
  lessons: Lesson[];
  /** Other notes in the level folder, e.g. lessons waiting for data. */
  notes: { title: string; html: string }[];
}

export type SavedStrategySource = 'Exam' | 'School' | 'Fund';

export interface SavedStrategy {
  id: string;
  source: SavedStrategySource;
  name: string;
  spec: Record<string, unknown>;
}

/**
 * Repo-relative links in the docs point at markdown files. Inside the app,
 * lesson links jump to the lesson's card; anything else becomes plain text.
 */
const renderer = new marked.Renderer();
renderer.link = ({ href, tokens }) => {
  const text = renderer.parser.parseInline(tokens);
  if (/^https?:/.test(href)) return `<a href="${href}" target="_blank" rel="noreferrer">${text}</a>`;
  // Lesson anchors look like "L2-03".
  const lesson = /level-(\d+)\/(\d+)-[^/]+\/lesson\.md$/.exec(href);
  if (lesson) return `<a href="/research/school#L${lesson[1]}-${lesson[2]}">${text}</a>`;
  return text;
};

export function renderMarkdown(md: string): string {
  return marked.parse(stripFrontmatter(md), { renderer, async: false }) as string;
}

function stripFrontmatter(md: string): string {
  return md.replace(/^---\r?\n[\s\S]*?\r?\n---\r?\n/, '');
}

/** A rigorous run's verdict wins; older lessons carry their status in the write-up's heading. */
function statusOf(md: string | undefined, results: LessonResults | null): LessonStatus {
  if (results?.verdict && STATUSES.has(results.verdict)) return results.verdict as LessonStatus;
  const first = md?.split('\n')[0] ?? '';
  const s = /\*\*(.+?)\*\*/.exec(first)?.[1];
  return s && STATUSES.has(s) ? (s as LessonStatus) : 'Not run';
}

function folderOf(path: string): string {
  return path.split('/').slice(-2, -1)[0]!;
}

function levelOf(path: string): number {
  return Number(/level-(\d+)/.exec(path)?.[1] ?? 0);
}

export const levels: Level[] = (() => {
  const byLevel = new Map<number, Level>();
  const ensure = (n: number): Level => {
    let level = byLevel.get(n);
    if (!level) {
      const readme = levelMd[`../../../docs/school/level-${n}/README.md`] ?? '';
      const title = /^# (.+)$/m.exec(readme)?.[1] ?? `Level ${n}`;
      level = { number: n, title, lessons: [], notes: [] };
      byLevel.set(n, level);
    }
    return level;
  };

  for (const [path, def] of Object.entries(lessonJson)) {
    const dir = path.slice(0, path.lastIndexOf('/'));
    const md = lessonMd[`${dir}/lesson.md`];
    const results = normalizeResults(lessonResults[`${dir}/results.json`]);
    ensure(levelOf(path)).lessons.push({
      ...def,
      folder: folderOf(path),
      status: statusOf(md, results),
      results,
      html: md ? renderMarkdown(md) : '',
    });
  }
  for (const [path, md] of Object.entries(levelMd)) {
    if (path.endsWith('/README.md')) continue;
    const title = /^# (.+)$/m.exec(md)?.[1] ?? path;
    ensure(levelOf(path)).notes.push({ title, html: renderMarkdown(md) });
  }

  const list = [...byLevel.values()].sort((a, b) => a.number - b.number);
  for (const level of list) level.lessons.sort((a, b) => a.id.localeCompare(b.id));
  return list;
})();

export const notebookHtml = renderMarkdown(notebookRaw);

/** The notebook cut at its `## ` headings, for a page that lists and searches its sections. */
export const notebookSections: { title: string; text: string; html: string }[] = stripFrontmatter(notebookRaw)
  .split(/\r?\n(?=## )/)
  .map((part, i) => {
    const title = i === 0 ? 'About the notebook' : (/^## (.+)$/m.exec(part)?.[1] ?? '').trim();
    const body = i === 0 ? part.replace(/^\s*# .*\r?\n/, '') : part.replace(/^## .*\r?\n/, '');
    return { title, text: body.toLowerCase(), html: renderMarkdown(body) };
  })
  .filter((s) => s.text.trim() !== '');

export const examHtml = renderMarkdown(examReadmeRaw);

/** The exam write-up cut at its `## ` headings (the opening is the forex-data note's lead-in). */
export const examSections: { title: string; html: string }[] = stripFrontmatter(examReadmeRaw)
  .split(/\r?\n(?=## )/)
  .slice(1)
  .map((part) => ({
    title: (/^## (.+)$/m.exec(part)?.[1] ?? '').trim(),
    html: renderMarkdown(part.replace(/^## .*\r?\n/, '')),
  }));
/** The school README's opening paragraph, without its title (the page has one). */
export const schoolIntroHtml = renderMarkdown((schoolReadmeRaw.split('\n## ')[0] ?? '').replace(/^# .*\r?\n/, ''));

export const blueprintHtml = renderMarkdown(blueprintRaw);

export const savedStrategies: SavedStrategy[] = Object.entries(strategyJson)
  .map(([path, spec]) => ({
    id: path.split('/').pop()!.replace(/\.json$/, ''),
    source: (path.includes('/exam/') ? 'Exam' : path.includes('/funds/') ? 'Fund' : 'School') as SavedStrategy['source'],
    name: typeof spec['name'] === 'string' ? spec['name'] : path,
    spec,
  }))
  .sort((a, b) => a.source.localeCompare(b.source) || a.name.localeCompare(b.name));
